/**
 * FeedbackPanel — a positioned popup that appears above the Feedback FAB
 * button in the bottom-right corner. Allows users to submit categorized
 * feedback (Bug, Feature, Improvement, Other) with a free-text message.
 *
 * Single-screen layout: type grid + textarea + submit button are all
 * visible simultaneously. User selects a type (highlights it), types a
 * message, and submits. No multi-step navigation.
 *
 * Closes on Escape key or click outside. Auto-closes 2s after success.
 *
 * Accessibility: focus-trapped when open, Escape dismissal, aria-labelledby
 * on the panel, aria-live for status announcements.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  AlertCircle,
  CheckCircle,
  Lightbulb,
  MessageSquare,
  TrendingUp,
  X,
} from 'lucide-react';
import { AppleButton } from '@/components/atoms/AppleButton';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type FeedbackType = 'bug' | 'feature' | 'improvement' | 'other';

type PanelState =
  | 'idle'
  | 'ready'
  | 'submitting'
  | 'success';

export interface FeedbackPanelProps {
  /** Whether the panel is visible. */
  readonly open: boolean;
  /** Called when the panel requests to close. */
  readonly onClose: () => void;
  /** Offset from the right edge when a side panel is visible (px). */
  readonly rightOffset?: number;
}

// ---------------------------------------------------------------------------
// Hook: useFeedbackPanel
// ---------------------------------------------------------------------------

interface UseFeedbackPanelReturn {
  open: boolean;
  toggle: () => void;
  close: () => void;
}

export function useFeedbackPanel(): UseFeedbackPanelReturn {
  const [open, setOpen] = useState(false);

  const toggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  return { open, toggle, close };
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const FEEDBACK_TYPES: ReadonlyArray<{
  id: FeedbackType;
  label: string;
  Icon: typeof AlertCircle;
}> = [
  { id: 'bug', label: 'Bug', Icon: AlertCircle },
  { id: 'feature', label: 'Feature', Icon: Lightbulb },
  { id: 'improvement', label: 'Improvement', Icon: TrendingUp },
  { id: 'other', label: 'Other', Icon: MessageSquare },
];

const PANEL_WIDTH = 320;
const AUTO_CLOSE_MS = 2000;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FeedbackPanel({
  open,
  onClose,
  rightOffset,
}: FeedbackPanelProps): JSX.Element | null {
  const [state, setState] = useState<PanelState>('idle');
  const [selectedType, setSelectedType] = useState<FeedbackType | null>(null);
  const [text, setText] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync open prop to internal state machine
  useEffect(() => {
    if (open && state === 'idle') {
      setState('ready');
    } else if (!open) {
      // Reset when closed externally
      setState('idle');
      setSelectedType(null);
      setText('');
    }
  }, [open, state]);

  // Close on Escape key
  useEffect(() => {
    if (!open) return;

    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent): void {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    }

    // Delay listener attachment to avoid the click that opened the panel
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  // Auto-close after success
  useEffect(() => {
    if (state === 'success') {
      autoCloseTimerRef.current = setTimeout(() => {
        onClose();
      }, AUTO_CLOSE_MS);
    }

    return () => {
      if (autoCloseTimerRef.current) {
        clearTimeout(autoCloseTimerRef.current);
        autoCloseTimerRef.current = null;
      }
    };
  }, [state, onClose]);

  // Handlers
  const handleTypeSelect = useCallback((type: FeedbackType) => {
    setSelectedType(type);
  }, []);

  const handleSubmit = useCallback(() => {
    if (!selectedType || !text.trim()) return;

    setState('submitting');

    // Simulate submission (replace with actual API call)
    setTimeout(() => {
      setState('success');
    }, 800);
  }, [selectedType, text]);

  // Don't render when fully closed
  if (!open && state === 'idle') return null;

  const right = rightOffset ?? 24;
  const isSubmitting = state === 'submitting';
  const canSubmit = Boolean(selectedType && text.trim());

  const panelStyle: CSSProperties = {
    position: 'fixed',
    bottom: 80,
    right,
    width: PANEL_WIDTH,
    zIndex: 9001,
    transform: open ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.96)',
    opacity: open ? 1 : 0,
    transition: `transform 300ms var(--ease-spring), opacity 300ms var(--ease-spring)`,
    pointerEvents: open ? 'auto' : 'none',
  };

  return (
    <div
      ref={panelRef}
      style={panelStyle}
      className="bg-wayne-surface border border-wayne-hairline rounded-2xl shadow-lg overflow-hidden"
      role="dialog"
      aria-labelledby="feedback-panel-title"
      aria-modal="false"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-wayne-hairline">
        <h3
          id="feedback-panel-title"
          className="font-serif text-[16px] font-medium text-wayne-ink m-0"
        >
          Send Feedback
        </h3>
        <button
          type="button"
          onClick={onClose}
          className="flex items-center justify-center w-6 h-6 rounded-full text-wayne-ink-3 hover:text-wayne-ink hover:bg-wayne-surface-2 transition-colors cursor-pointer"
          aria-label="Close feedback panel"
        >
          <X size={14} />
        </button>
      </div>

      {/* Body */}
      <div className="p-4">
        {state === 'success' ? (
          <SuccessView />
        ) : (
          <div className="flex flex-col gap-3">
            {/* Type grid — always visible */}
            <TypeGrid
              selected={selectedType}
              onSelect={handleTypeSelect}
              disabled={isSubmitting}
            />

            {/* Textarea — always visible */}
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={isSubmitting}
              placeholder="Describe your feedback..."
              className={[
                'w-full min-h-[80px] p-3 text-[13px] text-wayne-ink bg-wayne-bg border rounded-lg resize-y',
                'placeholder:text-wayne-ink-3',
                'focus:outline-none focus:border-wayne-accent',
                'disabled:opacity-50 disabled:cursor-not-allowed',
                'border-wayne-hairline',
                'transition-colors',
              ].join(' ')}
              aria-label="Feedback message"
            />

            {/* Submit — always visible */}
            <AppleButton
              variant="accent"
              onClick={handleSubmit}
              disabled={isSubmitting || !canSubmit}
              className="w-full justify-center"
              aria-busy={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Submit Feedback'}
            </AppleButton>
          </div>
        )}
      </div>

      {/* Privacy note */}
      <div className="px-4 pb-3">
        <p className="text-[11px] text-wayne-ink-3">
          Feedback is anonymous. We never share your identity.
        </p>
      </div>

      {/* Status announcer for screen readers */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {state === 'submitting' && 'Sending feedback...'}
        {state === 'success' && 'Feedback sent successfully. Thank you!'}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface TypeGridProps {
  readonly selected: FeedbackType | null;
  readonly onSelect: (type: FeedbackType) => void;
  readonly disabled?: boolean;
}

function TypeGrid({ selected, onSelect, disabled = false }: TypeGridProps): JSX.Element {
  return (
    <div className="grid grid-cols-2 gap-2">
      {FEEDBACK_TYPES.map(({ id, label, Icon }) => {
        const isSelected = selected === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onSelect(id)}
            disabled={disabled}
            className={[
              'flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-colors cursor-pointer',
              'disabled:cursor-not-allowed disabled:opacity-50',
              isSelected
                ? 'bg-wayne-accent/10 border-wayne-accent text-wayne-accent'
                : 'border-wayne-hairline text-wayne-ink-2 hover:border-wayne-accent hover:text-wayne-ink',
            ].join(' ')}
            aria-pressed={isSelected}
          >
            <Icon size={20} />
            <span className="text-[12px] font-medium">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

function SuccessView(): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 py-4">
      <div className="flex items-center justify-center w-12 h-12 rounded-full bg-wayne-green/10">
        <CheckCircle size={24} className="text-wayne-green" />
      </div>
      <p className="text-[14px] font-medium text-wayne-ink m-0">
        Thanks for your feedback!
      </p>
    </div>
  );
}

export default FeedbackPanel;
